import { isPlatformBrowser } from '@angular/common';
import {  ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { debounceTime, Observable, filter, share, startWith, Subject, takeUntil, switchMap } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FormControl } from '@angular/forms';
import { AcvisService } from '@app/services/acvis.service';
import { HotelFiltersService } from '@app/services/hotelfilters.service';
import { ParamFilters } from '@app/models/filtermodels/ParamFilters';

interface AirItem {
    code: string;
    name: string;
}

@Component({
    selector: 'clever-airport',
    templateUrl: './airport.component.html',
    styleUrls: ['./airport.component.scss'],
    animations: [
        trigger('zoom', [
            state('leftBottom', style({ transform: 'scale(0.3)', transformOrigin: 'left bottom', opacity: '0' })),
            state('default', style({ transform: 'scale(1)', opacity: '1' })),
            transition('* <=> default', animate('10ms cubic-bezier(0.25,1,.25,1)'))
        ])
    ],
    standalone: false
})
export class AirportComponent implements OnInit, OnDestroy {
    @Input() title: string = 'Zielflughafen';
    @Input() mode: string;
    @Input() isArrival: boolean = true;
    @Input() airports: AirItem [];
    protected unsubscribe$ = new Subject();
    isBrowser: boolean = false;
    showModal: boolean = false;
    focus: boolean = true;
    media: string = 'lg';
    origin = 'default';
    zielText = 'Alle';
    searchObj: Observable<any>;
    mySearchControl = new FormControl();
    selectedList: any[] = [];
    selectedListOnlyCode: string[] = [];
    slicenbr: number = 5;

    constructor(
        protected mediaObserver: BreakpointObserver,
        protected cdr: ChangeDetectorRef,
        protected acvisService: AcvisService,
        protected render: Renderer2,
        protected hotelService: HotelFiltersService,
        @Inject(PLATFORM_ID) protected platformId: any,
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    ngOnInit(): void {
        if (this.isBrowser) {
            this.initMedia();
            this.initFilterSub();
            this.initSearchChange();
        }
    }

    initSearchChange() {
        const filterShare = this.mySearchControl.valueChanges.pipe(
            startWith(''),
            debounceTime(100),
            filter(value => (typeof value === 'string' && value?.length > 0)),
            share()
        );

        this.searchObj = filterShare.pipe(
            switchMap(value => this.acvisService.request({
                searchValue: value,
                inAll: true
            }, 'airoportListCompletions'))
        );
    }

    initMedia() {
       if (this.mediaObserver.isMatched(Breakpoints.XSmall)) {
            this.media = 'xs';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Small)) {
            this.media = 'sm';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Medium)) {
            this.media = 'md';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Large)) {
            this.media = 'lg';
        }
        if (this.mediaObserver.isMatched(Breakpoints.XLarge)) {
            this.media = 'xl';
        }
        this.cdr.detectChanges();
    }

    
    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    openModal(ev) {
        ev?.stopPropagation();
        this.showModal = true;
        this.zoomin('leftBottom');
    }

    openModalMobile(ev) {
        // this.render.addClass(document.body, 'model-open');
        ev?.stopPropagation();
        this.showModal = true;
        this.zoomin('leftBottom');
    }

    closeModal() {
        this.showModal = false;
        this.mySearchControl.setValue('');
        this.cdr.detectChanges();
    }

    closeModalMobile() {
        this.showModal = false;
        // this.render.removeClass(document.body, 'model-open');
        this.mySearchControl.setValue('');
        this.cdr.detectChanges();
    }

    sendSelect() {
        if (this.isArrival) {
            this.hotelService.arrivalAirportList(this.selectedList, this.mode !== 'home');
        } else {
            this.hotelService.departurAirportList(this.selectedList, this.mode !== 'home');
        }
        this.closeModal();
    }

    zoomin(position: any) {
        this.origin = position;
        this.cdr.detectChanges();
        setTimeout(() => {
            this.origin = 'default';
            this.cdr.detectChanges();
        });
    }

    clearText() {
        this.mySearchControl.setValue('');
        this.focus = false;
        this.cdr.detectChanges();
        setTimeout(() => {
            this.focus = true;
            this.cdr.detectChanges();
        }, 50);
    }

    selectValue(item) {
        this.selectedList.push({
            name: item.name,
            code: item.code,
        });
        this.zielText = this.selectedList.length + ' Flughäfen';
    }

    isNotInlist(item): boolean {
        for (let i = 0; i < this.selectedList.length; i++) {
            if (this.selectedList[i].code === item?.code) {
                return false;
            }
        }
        return true;
    }

    removeAllSelected() {
        this.selectedList = [];
        this.zielText = 'Alle';
    }

    removeFromSelected(k) {
        if (this.selectedList[k]) {
            this.selectedList.splice(k, 1);
        }
        this.zielText = this.selectedList.length > 0 ? this.selectedList.length + ' Flughäfen' : 'Alle';
    }

    initFilterSub(): void {
        this.hotelService.getfilters$.pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe((filter: ParamFilters) => {
            this.selectedList = [];
            this.selectedListOnlyCode = [];
            if (this.isArrival) {
                if (filter.arrivalAirportList?.length > 0) {
                    for (let i = 0; i < filter.arrivalAirportList.length; i++) {
                        this.selectedList.push({
                            code: filter.arrivalAirportList[i].code,
                            name: filter.arrivalAirportList[i].name,
                        });
                        this.selectedListOnlyCode.push(filter.arrivalAirportList[i].code);
                    }
                }
            } else {
                if (filter.airportsResult?.length > 0) {
                    for (let i = 0; i < filter.airportsResult.length; i++) {
                        this.selectedList.push({
                            code: filter.airportsResult[i].code,
                            name: filter.airportsResult[i].name,
                        });
                        this.selectedListOnlyCode.push(filter.airportsResult[i].code);
                    }
                }
            }
            this.zielText = this.selectedList.length > 0 ? this.selectedList.length + ' Flughäfen' : 'Alle';
            this.cdr.detectChanges();
        });
    }

    sortArray(array): any[] {
        if (array?.length > 0) {
            array?.sort((a, b) => {
                a = a.name.toLowerCase();
                b = b.name.toLowerCase();

                return a < b ? -1 : a > b ? 1 : 0;
            });
        }
        return array;
    }

    showMore(all) {
        this.slicenbr = all;
    }

    selected(item): void {
        if (this.selectedListOnlyCode.indexOf(item.code) >= 0) {
            for (let i = 0; i < this.selectedList.length; i++) {
                if (this.selectedList[i].code === item.code) {
                    this.selectedList.splice(i, 1);
                    i < this.selectedList.length + 1;
                }
            }
        } else {
            this.selectedList.push({
                name: item.name,
                code: item.code,
            });
        }
        if (this.isArrival) {
            this.hotelService.arrivalAirportList(this.selectedList, true);
        } else {
            this.hotelService.departurAirportList(this.selectedList, true);
        }
        this.cdr.detectChanges();
    }
}
